/**
 * VectorAttributeTable — Card 4 §"Attribute table" requirement.
 *
 * Minimal token-driven HTML `<table>` per WP-4 blocker policy: ships
 * without the (planned) `<DwTable>` design-system primitive but stays
 * tokenized for radius / density / typography / surface levels.
 *
 * Keyboard contract:
 *   - `ArrowDown` / `ArrowUp` — move the focused row by ±1
 *   - `Home` / `End` — jump to the first / last row on the current page
 *   - `Enter` — emit `onSelect(feature)` for the focused row
 *   - `PageDown` / `PageUp` — move ±1 page
 *
 * The component is uncontrolled w.r.t. pagination; the active page
 * and page size live in local state because the WP-4 acceptance only
 * asks for "renders rows / paging", not server-side paging.
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Button, Icon, Stack, Text, tokens } from '@/design';
import type { VectorExportFeature } from '@geosoft/viewer-sdk';
import {
  attributeRowKey,
  cellInputValue,
  frozenLeftOffset,
  HEADER_ACTION_HINT,
  parseEditedCellValue,
  type SortLevel,
} from './attributeTableModel';

export type VectorAttributeRow = VectorExportFeature & {
  readonly _rowKey?: string;
};

export interface VectorAttributeTableProps {
  /** Pre-filtered features to render. */
  readonly features: ReadonlyArray<VectorAttributeRow>;
  /** Visible columns (sourced by the ToolCard from the feature property keys). */
  readonly columns: ReadonlyArray<string>;
  /** Optional id-column hint — these columns are rendered in mono. */
  readonly idColumns?: ReadonlyArray<string>;
  /** Total number of features (when paged source carries more than `features.length`). */
  readonly totalCount?: number;
  /** Active loading / error state. */
  readonly state?: 'idle' | 'loading' | 'error' | 'empty';
  /** Localized error message when `state === 'error'`. */
  readonly errorMessage?: string;
  readonly pageSize?: 10 | 25 | 50;
  /** Fires when a row is activated (click or Enter). */
  readonly onSelect?: (feature: VectorAttributeRow) => void;
  /** Currently selected feature id (for visual highlight). */
  readonly selectedFeatureId?: string | number | null;
  /** Stable `_rowKey` values selected through the checkbox gutter. */
  readonly selectedRowKeys?: ReadonlySet<string>;
  readonly onToggleRowSelection?: (
    rowKey: string,
    feature: VectorAttributeRow,
    selected: boolean,
  ) => void;
  readonly onToggleAllRows?: (
    rows: ReadonlyArray<VectorAttributeRow>,
    selected: boolean,
  ) => void;
  /** `dialog` fills a popup; `inline` is the compact sidebar embed. */
  readonly variant?: 'inline' | 'dialog';
  readonly hideHeading?: boolean;
  /** Active multi-field sort — first level is shown on the header. */
  readonly sortSpec?: ReadonlyArray<SortLevel>;
  /** Visible columns pinned to the left while scrolling. */
  readonly frozenColumns?: ReadonlyArray<string>;
  /** Double-click / Shift+F10 on a column header. */
  readonly onHeaderMenu?: (column: string, position: { x: number; y: number }) => void;
  /** Session-only cell edit from double-clicking a row. */
  readonly onUpdateCell?: (row: VectorAttributeRow, column: string, value: unknown) => void;
  /** Append an empty row. */
  readonly onAddRow?: () => void;
  readonly toolbar?: ReactNode;
  readonly 'data-testid'?: string;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
const EMPTY_SELECTED_ROW_KEYS: ReadonlySet<string> = new Set<string>();

export function VectorAttributeTable({
  features,
  columns,
  idColumns,
  totalCount,
  state = 'idle',
  errorMessage,
  pageSize: pageSizeProp = 10,
  onSelect,
  selectedFeatureId = null,
  selectedRowKeys = EMPTY_SELECTED_ROW_KEYS,
  onToggleRowSelection,
  onToggleAllRows,
  variant = 'inline',
  hideHeading = false,
  sortSpec,
  frozenColumns = [],
  onHeaderMenu,
  onUpdateCell,
  onAddRow,
  toolbar,
  'data-testid': testId,
}: VectorAttributeTableProps) {
  const headingId = useId();
  const tableId = useId();
  const [pageSize, setPageSize] = useState<10 | 25 | 50>(pageSizeProp);
  const [pageIndex, setPageIndex] = useState(0);
  const [focusedRowIndex, setFocusedRowIndex] = useState(0);
  const [hoveredHeader, setHoveredHeader] = useState<string | null>(null);
  const [headerHintPos, setHeaderHintPos] = useState<{ x: number; y: number } | null>(null);
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Record<string, string> | null>(null);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const previousCountRef = useRef(features.length);

  const pageCount = Math.max(1, Math.ceil(features.length / pageSize));
  const safePageIndex = Math.min(pageIndex, pageCount - 1);
  const pageStart = safePageIndex * pageSize;
  const pageRows = features.slice(pageStart, pageStart + pageSize);
  const idColumnSet = useMemo(
    () => new Set<string>([...(idColumns ?? []), 'id']),
    [idColumns],
  );
  const primarySort = sortSpec?.[0];
  const canEdit = Boolean(onUpdateCell);
  const canSelectRows = Boolean(onToggleRowSelection);
  const selectionGutterWidth = canSelectRows ? SELECTION_GUTTER_WIDTH : 0;
  const gutterWidth =
    selectionGutterWidth + (canEdit ? EDIT_GUTTER_WIDTH : 0);
  const selectedDisplayedCount = features.reduce(
    (count, feature, index) =>
      count + (selectedRowKeys.has(attributeRowKey(feature, index)) ? 1 : 0),
    0,
  );
  const allDisplayedSelected =
    features.length > 0 && selectedDisplayedCount === features.length;
  const someDisplayedSelected =
    selectedDisplayedCount > 0 && !allDisplayedSelected;

  const sortKey = sortSpec?.map((level) => `${level.field}:${level.direction}`).join('|') ?? '';

  useEffect(() => {
    setPageIndex(0);
    setFocusedRowIndex(0);
  }, [sortKey]);

  useEffect(() => {
    if (features.length > previousCountRef.current) {
      const last = features[features.length - 1];
      setPageIndex(Math.max(0, Math.ceil(features.length / pageSize) - 1));
      if (last && onUpdateCell) {
        const key = attributeRowKey(last, features.length - 1);
        setEditingRowKey(key);
        const draft: Record<string, string> = {};
        for (const column of columns) {
          draft[column] = cellInputValue(last.properties?.[column]);
        }
        setEditDraft(draft);
        setFocusedRowIndex((features.length - 1) % pageSize);
      }
    }
    previousCountRef.current = features.length;
  }, [columns, features.length, onUpdateCell, pageSize]);

  useEffect(() => {
    if (focusedRowIndex >= pageRows.length) {
      setFocusedRowIndex(Math.max(0, pageRows.length - 1));
    }
  }, [focusedRowIndex, pageRows.length]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLTableSectionElement>) => {
      if (pageRows.length === 0) return;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedRowIndex((prev) => Math.min(prev + 1, pageRows.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedRowIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Home':
          event.preventDefault();
          setFocusedRowIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setFocusedRowIndex(pageRows.length - 1);
          break;
        case 'PageDown':
          event.preventDefault();
          setPageIndex((prev) => Math.min(prev + 1, pageCount - 1));
          setFocusedRowIndex(0);
          break;
        case 'PageUp':
          event.preventDefault();
          setPageIndex((prev) => Math.max(prev - 1, 0));
          setFocusedRowIndex(0);
          break;
        case 'Enter':
        case ' ':
          if (editingRowKey) return;
          event.preventDefault();
          if (onSelect) {
            const feature = pageRows[focusedRowIndex];
            if (feature) onSelect(feature);
          }
          break;
        case 'Escape':
          if (editingRowKey) {
            event.preventDefault();
            setEditingRowKey(null);
            setEditDraft(null);
          }
          break;
        default:
          break;
      }
    },
    [editingRowKey, focusedRowIndex, onSelect, pageCount, pageRows],
  );

  const reportedTotal = totalCount ?? features.length;

  return (
    <section
      aria-labelledby={headingId}
      data-testid={testId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[2],
        minHeight: 0,
        flex: variant === 'dialog' ? '1 1 auto' : undefined,
      }}
    >
      {hideHeading ? (
        <Text variant="caption" as="span" id={headingId} style={{ color: tokens.color.ink.secondary }}>
          <strong>{reportedTotal}</strong> rows
        </Text>
      ) : (
        <Stack direction="row" align="center" gap={2}>
          <Icon name="table" size={20} decorative />
          <Text variant="subtitle" as="h3" id={headingId} style={{ margin: 0 }}>
            Attribute table
          </Text>
          <span style={{ flex: '1 1 auto' }} />
          <Text variant="caption" as="span">
            <strong>{reportedTotal}</strong> rows
          </Text>
        </Stack>
      )}
      {toolbar}

      {state === 'loading' ? (
        <Stack direction="row" gap={2} align="center" data-testid={`${testId}-loading`}>
          <Icon name="pending" size={20} decorative />
          <Text variant="body" as="span">Loading features…</Text>
        </Stack>
      ) : null}
      {state === 'error' ? (
        <Stack direction="row" gap={2} align="center" data-testid={`${testId}-error`}>
          <Icon name="error" size={20} decorative />
          <Text variant="body" as="span">
            {errorMessage ?? 'Failed to load features.'}
          </Text>
        </Stack>
      ) : null}
      {state === 'empty' || (state === 'idle' && features.length === 0) ? (
        <Stack direction="row" gap={2} align="center" data-testid={`${testId}-empty`}>
          <Icon name="info" size={20} decorative />
          <Text variant="body" as="span">
            No features available for this layer yet.
          </Text>
        </Stack>
      ) : null}

      {pageRows.length > 0 ? (
        <div
          role="region"
          aria-labelledby={headingId}
          style={{
            border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
            borderRadius: tokens.radius.md,
            overflowX: 'auto',
            overflowY: 'auto',
            maxHeight: variant === 'dialog' ? 'none' : 360,
            flex: variant === 'dialog' ? '1 1 auto' : undefined,
            minHeight: variant === 'dialog' ? 0 : undefined,
            background: tokens.color.surface[1],
          }}
        >
          <table
            id={tableId}
            data-testid={`${testId}-table`}
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.size.sm,
              color: tokens.color.ink.primary,
            }}
          >
            <caption
              style={{
                ...visuallyHiddenStyle(),
              }}
            >
              Vector layer feature attributes; use Arrow keys to move and Enter to select.
            </caption>
            <thead>
              <tr style={{ background: tokens.color.surface[2] }}>
                {canSelectRows ? (
                  <th
                    scope="col"
                    aria-label="Row selection"
                    style={gutterHeaderStyle({ left: 0, width: SELECTION_GUTTER_WIDTH })}
                  >
                    <input
                      type="checkbox"
                      aria-label="Select all rows"
                      aria-checked={someDisplayedSelected ? 'mixed' : allDisplayedSelected}
                      checked={allDisplayedSelected}
                      ref={(node) => {
                        if (node) node.indeterminate = someDisplayedSelected;
                      }}
                      onChange={(event) =>
                        onToggleAllRows?.(features, event.target.checked)
                      }
                    />
                  </th>
                ) : null}
                {canEdit ? (
                  <th
                    scope="col"
                    aria-label="Row edit status"
                    style={gutterHeaderStyle({
                      left: selectionGutterWidth,
                      width: EDIT_GUTTER_WIDTH,
                    })}
                  />
                ) : null}
                {columns.map((column) => {
                  const freezeLeft = frozenLeftOffset(frozenColumns, column, FROZEN_COL_WIDTH);
                  const isSorted = primarySort?.field === column;
                  return (
                    <th
                      key={column}
                      scope="col"
                      data-testid={testId ? `${testId}-header-${column}` : undefined}
                      onMouseEnter={(event) => {
                        const rect = event.currentTarget.getBoundingClientRect();
                        setHoveredHeader(column);
                        setHeaderHintPos({ x: rect.left + rect.width / 2, y: rect.top });
                      }}
                      onMouseLeave={() => {
                        setHoveredHeader((current) => (current === column ? null : current));
                        setHeaderHintPos(null);
                      }}
                      onDoubleClick={(event) => {
                        if (!onHeaderMenu) return;
                        event.preventDefault();
                        onHeaderMenu(column, { x: event.clientX, y: event.clientY });
                      }}
                      style={headerCellStyle({
                        frozen: freezeLeft !== null,
                        left: freezeLeft === null ? 0 : freezeLeft + gutterWidth,
                        interactive: Boolean(onHeaderMenu),
                      })}
                    >
                      <button
                        type="button"
                        aria-label={`${column} column actions`}
                        aria-haspopup="menu"
                        title={onHeaderMenu ? HEADER_ACTION_HINT : undefined}
                        data-sort-direction={isSorted ? primarySort.direction : undefined}
                        onDoubleClick={(event) => {
                          if (!onHeaderMenu) return;
                          event.preventDefault();
                          event.stopPropagation();
                          onHeaderMenu(column, { x: event.clientX, y: event.clientY });
                        }}
                        onKeyDown={(event) => {
                          if (!onHeaderMenu) return;
                          if (event.key === 'F10' && event.shiftKey) {
                            event.preventDefault();
                            const rect = event.currentTarget.getBoundingClientRect();
                            onHeaderMenu(column, { x: rect.left, y: rect.bottom });
                          }
                        }}
                        style={headerButtonStyle(Boolean(onHeaderMenu))}
                      >
                        <span>{column}</span>
                        {isSorted ? (
                          <Icon
                            name="swap_vert"
                            size={16}
                            decorative
                            aria-label={primarySort.direction === 'asc' ? 'Sorted ascending' : 'Sorted descending'}
                          />
                        ) : null}
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody
              ref={tbodyRef}
              role="rowgroup"
              tabIndex={0}
              aria-label="Feature rows"
              aria-activedescendant={`row-${pageStart + focusedRowIndex}`}
              onKeyDown={handleKeyDown}
              style={{ outline: 'none' }}
            >
              {pageRows.map((feature, indexOnPage) => {
                const featureKey = attributeRowKey(feature, pageStart + indexOnPage);
                const isFocused = indexOnPage === focusedRowIndex;
                const isSelected =
                  selectedFeatureId !== null && String(selectedFeatureId) === String(feature.id);
                const isChecked = selectedRowKeys.has(featureKey);
                const isEditing = canEdit && editingRowKey === featureKey;
                return (
                  <tr
                    id={`row-${pageStart + indexOnPage}`}
                    key={featureKey}
                    data-testid={`${testId}-row`}
                    data-editing={isEditing ? 'true' : undefined}
                    role="row"
                    aria-selected={isSelected || isChecked}
                    onClick={() => {
                      setFocusedRowIndex(indexOnPage);
                      if (onSelect) onSelect(feature);
                    }}
                    onDoubleClick={(event) => {
                      if (!canEdit) return;
                      event.preventDefault();
                      setFocusedRowIndex(indexOnPage);
                      setEditingRowKey(featureKey);
                      const draft: Record<string, string> = {};
                      for (const column of columns) {
                        draft[column] = cellInputValue(feature.properties?.[column]);
                      }
                      setEditDraft(draft);
                    }}
                    style={{
                      background: isEditing
                        ? tokens.color.accent.subtle
                        : isSelected || isChecked
                          ? tokens.color.accent.subtle
                          : isFocused
                            ? tokens.color.surface[3]
                            : indexOnPage % 2 === 1
                              ? tokens.color.surface[2]
                              : tokens.color.surface[1],
                      cursor: canEdit ? 'text' : 'pointer',
                    }}
                  >
                    {canSelectRows ? (
                      <td
                        style={gutterCellStyle({
                          left: 0,
                          width: SELECTION_GUTTER_WIDTH,
                          selected: isChecked,
                          striped:
                            !isSelected
                            && !isChecked
                            && !isFocused
                            && !isEditing
                            && indexOnPage % 2 === 1,
                        })}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          aria-label={`Select row ${pageStart + indexOnPage + 1}`}
                          onClick={(event) => event.stopPropagation()}
                          onDoubleClick={(event) => event.stopPropagation()}
                          onChange={(event) => {
                            event.stopPropagation();
                            onToggleRowSelection?.(
                              featureKey,
                              feature,
                              event.target.checked,
                            );
                          }}
                        />
                      </td>
                    ) : null}
                    {canEdit ? (
                      <td
                        style={gutterCellStyle({
                          left: selectionGutterWidth,
                          width: EDIT_GUTTER_WIDTH,
                          editing: isEditing,
                          striped:
                            !isSelected
                            && !isChecked
                            && !isFocused
                            && !isEditing
                            && indexOnPage % 2 === 1,
                        })}
                      >
                        {isEditing ? (
                          <Icon name="edit" size={16} decorative />
                        ) : null}
                      </td>
                    ) : null}
                    {columns.map((column) => {
                      const raw = feature.properties?.[column];
                      const isIdColumn = idColumnSet.has(column);
                      const freezeLeft = frozenLeftOffset(frozenColumns, column, FROZEN_COL_WIDTH);
                      return (
                        <td
                          key={column}
                          style={cellStyle({
                            mono: isIdColumn,
                            frozen: freezeLeft !== null,
                            left: freezeLeft === null ? 0 : freezeLeft + gutterWidth,
                            striped:
                              !isSelected
                              && !isChecked
                              && !isFocused
                              && !isEditing
                              && indexOnPage % 2 === 1,
                            editing: isEditing,
                          })}
                        >
                          {isEditing ? (
                            <input
                              aria-label={`Edit ${column}`}
                              value={editDraft?.[column] ?? cellInputValue(raw)}
                              onChange={(event) => {
                                const nextValue = event.target.value;
                                setEditDraft((prev) => ({ ...(prev ?? {}), [column]: nextValue }));
                              }}
                              onBlur={() => {
                                const nextValue = parseEditedCellValue(
                                  editDraft?.[column] ?? cellInputValue(raw),
                                  raw,
                                );
                                onUpdateCell?.(feature, column, nextValue);
                              }}
                              onKeyDown={(event) => {
                                if (event.key === 'Escape') {
                                  event.preventDefault();
                                  setEditingRowKey(null);
                                  setEditDraft(null);
                                }
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  const nextValue = parseEditedCellValue(
                                    editDraft?.[column] ?? cellInputValue(raw),
                                    raw,
                                  );
                                  onUpdateCell?.(feature, column, nextValue);
                                  setEditingRowKey(null);
                                  setEditDraft(null);
                                }
                                event.stopPropagation();
                              }}
                              onClick={(event) => event.stopPropagation()}
                              style={editInputStyle(isIdColumn)}
                            />
                          ) : (
                            formatCell(raw)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {features.length > 0 || onAddRow ? (
        <Stack direction="row" align="center" gap={2} justify="space-between" style={{ flexWrap: 'wrap' }}>
          <Stack direction="row" align="center" gap={2}>
            {onAddRow ? (
              <Button
                variant="secondary"
                onClick={onAddRow}
                data-testid={testId ? `${testId}-add-row` : undefined}
              >
                Click to add new row
              </Button>
            ) : null}
            {features.length > 0 ? (
              <label
                style={{
                  fontFamily: tokens.font.family.ui,
                  fontSize: tokens.font.size.xs,
                  color: tokens.color.ink.secondary,
                  display: 'inline-flex',
                  gap: tokens.space[1],
                  alignItems: 'center',
                }}
              >
                Rows per page
                <select
                  aria-label="Rows per page"
                  value={pageSize}
                  onChange={(event) => {
                    const next = Number(event.target.value) as 10 | 25 | 50;
                    setPageSize(next);
                    setPageIndex(0);
                    setFocusedRowIndex(0);
                  }}
                  style={selectStyle()}
                >
                  {PAGE_SIZE_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </Stack>
          {features.length > 0 ? (
            <Stack direction="row" align="center" gap={1}>
              <PageButton
                label="Previous page"
                icon="arrow_back"
                disabled={safePageIndex === 0}
                onClick={() => {
                  setPageIndex((prev) => Math.max(prev - 1, 0));
                  setFocusedRowIndex(0);
                }}
              />
              <Text variant="caption" as="span" data-testid={`${testId}-page`}>
                Page <strong>{safePageIndex + 1}</strong> / {pageCount}
              </Text>
              <PageButton
                label="Next page"
                icon="arrow_forward"
                disabled={safePageIndex >= pageCount - 1}
                onClick={() => {
                  setPageIndex((prev) => Math.min(prev + 1, pageCount - 1));
                  setFocusedRowIndex(0);
                }}
              />
            </Stack>
          ) : null}
        </Stack>
      ) : null}
      {onHeaderMenu && hoveredHeader && headerHintPos && typeof document !== 'undefined'
        ? createPortal(
            <span
              role="tooltip"
              data-testid={testId ? `${testId}-header-hint` : undefined}
              style={{
                position: 'fixed',
                left: headerHintPos.x,
                top: headerHintPos.y - tokens.space[1],
                transform: 'translate(-50%, -100%)',
                padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                background: tokens.color.overlay.tooltip,
                color: tokens.color.ink.primary,
                borderRadius: tokens.radius.sm,
                fontFamily: tokens.font.family.ui,
                fontSize: tokens.font.size.xs,
                zIndex: tokens.zIndex.toast,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                boxShadow: tokens.elevation[2],
              }}
            >
              {HEADER_ACTION_HINT}
            </span>,
            document.body,
          )
        : null}
    </section>
  );
}

interface PageButtonProps {
  readonly label: string;
  readonly icon: 'arrow_back' | 'arrow_forward';
  readonly disabled: boolean;
  readonly onClick: () => void;
}

function PageButton({ label, icon, disabled, onClick }: PageButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        appearance: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: tokens.density.touchMin,
        height: tokens.density.touchMin,
        background: 'transparent',
        border: 0,
        color: tokens.color.ink.primary,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        borderRadius: tokens.radius.sm,
      }}
    >
      <Icon name={icon} size={20} decorative />
    </button>
  );
}

const FROZEN_COL_WIDTH = 160;
const EDIT_GUTTER_WIDTH = 40;
const SELECTION_GUTTER_WIDTH = 40;

function headerCellStyle({
  frozen,
  left,
  interactive,
}: {
  frozen?: boolean;
  left?: number;
  interactive?: boolean;
} = {}) {
  return {
    textAlign: 'left',
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.xs,
    fontWeight: tokens.font.weight.semibold,
    color: tokens.color.ink.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    paddingInline: tokens.space[2],
    paddingBlock: tokens.space[2],
    borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    whiteSpace: 'nowrap',
    position: frozen ? ('sticky' as const) : ('relative' as const),
    left: frozen ? left : undefined,
    zIndex: frozen ? 2 : undefined,
    background: tokens.color.surface[2],
    minWidth: FROZEN_COL_WIDTH,
    cursor: interactive ? ('pointer' as const) : undefined,
    userSelect: 'none' as const,
  } as const;
}

function headerButtonStyle(interactive: boolean) {
  return {
    appearance: 'none' as const,
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space[1],
    width: '100%',
    background: 'transparent',
    border: 0,
    color: 'inherit',
    font: 'inherit',
    textTransform: 'inherit' as const,
    letterSpacing: 'inherit',
    cursor: interactive ? ('pointer' as const) : ('default' as const),
    padding: 0,
    minHeight: tokens.density.touchMin,
  };
}

function gutterHeaderStyle({
  left,
  width,
}: {
  left: number;
  width: number;
}) {
  return {
    width,
    minWidth: width,
    position: 'sticky' as const,
    left,
    zIndex: 3,
    padding: 0,
    textAlign: 'center' as const,
    background: tokens.color.surface[2],
    borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
  };
}

function gutterCellStyle({
  editing,
  selected,
  striped,
  left,
  width,
}: {
  editing?: boolean;
  selected?: boolean;
  striped: boolean;
  left: number;
  width: number;
}) {
  return {
    width,
    minWidth: width,
    position: 'sticky' as const,
    left,
    zIndex: 2,
    textAlign: 'center' as const,
    padding: 0,
    borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[2]}`,
    background: editing
      ? tokens.color.accent.primary
      : selected
        ? tokens.color.accent.subtle
      : striped
        ? tokens.color.surface[2]
        : tokens.color.surface[1],
    color: editing ? tokens.color.accent.on : tokens.color.ink.secondary,
  };
}

function cellStyle({
  mono,
  frozen,
  left,
  striped,
  editing,
}: {
  mono: boolean;
  frozen?: boolean;
  left?: number;
  striped?: boolean;
  editing?: boolean;
}) {
  return {
    paddingInline: tokens.space[2],
    paddingBlock: tokens.space[2],
    borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[2]}`,
    fontFamily: mono ? tokens.font.family.mono : tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
    color: tokens.color.ink.primary,
    height: tokens.density.rowDefault,
    minWidth: FROZEN_COL_WIDTH,
    maxWidth: tokens.layout.popoverLg,
    whiteSpace: 'nowrap' as const,
    overflow: editing ? 'visible' : 'hidden',
    textOverflow: editing ? undefined : 'ellipsis',
    position: frozen ? ('sticky' as const) : undefined,
    left: frozen ? left : undefined,
    zIndex: frozen ? 1 : undefined,
    background: frozen
      ? editing
        ? tokens.color.accent.subtle
        : striped
          ? tokens.color.surface[2]
          : tokens.color.surface[1]
      : undefined,
  } as const;
}

function editInputStyle(mono: boolean) {
  return {
    width: '100%',
    minHeight: tokens.density.rowDense,
    background: tokens.color.surface[0],
    color: tokens.color.ink.primary,
    border: `${tokens.border.width.thin}px solid ${tokens.color.accent.primary}`,
    borderRadius: tokens.radius.input,
    paddingInline: tokens.space[1],
    fontFamily: mono ? tokens.font.family.mono : tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  } as const;
}

function selectStyle() {
  return {
    minHeight: tokens.density.rowDense,
    background: tokens.color.surface[2],
    color: tokens.color.ink.primary,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.input,
    paddingInline: tokens.space[1],
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  } as const;
}

function visuallyHiddenStyle() {
  return {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
    border: 0,
  } as const;
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value.toString();
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
